import { useState } from 'react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Toaster, toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_elite-clarity/artifacts/jd30f2me_runner.jpg";

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Apply = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    environment: '',
    drawn_to_paragon: '',
    website: '', // Honeypot field
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Environment validation
    if (!formData.environment.trim()) {
      newErrors.environment = 'Please tell us about your performance environment';
    }
    
    // Drawn to Paragon validation
    if (!formData.drawn_to_paragon.trim()) {
      newErrors.drawn_to_paragon = 'Please tell us what drew you to Paragon';
    } else if (formData.drawn_to_paragon.trim().length < 10) {
      newErrors.drawn_to_paragon = 'Please provide a bit more detail (at least 10 characters)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please check the form for errors.');
      return;
    }

    setIsSubmitting(true);

    try {
      await axios.post(`${API}/applications`, formData);
      setIsSubmitted(true);
      toast.success('Your message has been sent successfully.');
    } catch (error) {
      console.error('Error submitting application:', error);
      if (error.response?.status === 429) {
        toast.error('Too many submissions. Please try again later.');
      } else if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to submit. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div data-testid="apply-success" className="bg-[#050505] min-h-screen flex items-center justify-center">
        <Toaster position="top-center" theme="dark" />
        <div className="max-w-[600px] mx-auto px-6 text-center">
          <AnimatedSection>
            <p className="caption-text mb-6">Message Received</p>
            <h1 className="heading-medium mb-8">
              Thank you for reaching out.
            </h1>
            <p className="body-text mb-4">
              We have received your message and will review it carefully.
            </p>
            <p className="body-text mb-8">
              You should receive a confirmation email shortly.
            </p>
            <p className="body-text mb-12">
              If there is alignment, we will be in touch to begin the conversation.
            </p>
            <div className="divider mx-auto" />
          </AnimatedSection>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="apply-page" className="bg-[#050505]">
      <Toaster position="top-center" theme="dark" />
      
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-end pb-12 pt-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE}
            alt="Runner"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 md:px-12 w-full">
          <AnimatedSection>
            <p className="caption-text mb-6">Contact</p>
            <h1 className="heading-large">Begin the Conversation</h1>
          </AnimatedSection>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 md:py-16">
        <div className="max-w-[800px] mx-auto px-6 md:px-12">
          <AnimatedSection>
            <p className="body-large mb-6">
              Paragon works with a very small number of individuals each year.
            </p>
            <p className="body-text mb-6">
              Most relationships begin through referral.
            </p>
            <p className="body-text">
              However, if this work resonates with you, we invite you to start a conversation.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Application Form */}
      <section className="section-padding pt-8">
        <div className="max-w-[700px] mx-auto px-6 md:px-12">
          <AnimatedSection>
            <form onSubmit={handleSubmit} data-testid="application-form" className="space-y-12">
              {/* Honeypot field - hidden from users, visible to bots */}
              <div className="absolute opacity-0 pointer-events-none" aria-hidden="true" style={{ position: 'absolute', left: '-9999px' }}>
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </div>

              {/* Name */}
              <div>
                <label htmlFor="name" className="form-label">
                  Name <span className="text-white/40">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  data-testid="input-name"
                  value={formData.name}
                  onChange={handleChange}
                  className={`form-input ${errors.name ? 'border-red-500/50' : ''}`}
                  placeholder="Your full name"
                  required
                />
                {errors.name && (
                  <p className="text-red-400 text-xs mt-2">{errors.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="form-label">
                  Email <span className="text-white/40">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  data-testid="input-email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`form-input ${errors.email ? 'border-red-500/50' : ''}`}
                  placeholder="your@email.com"
                  required
                />
                {errors.email && (
                  <p className="text-red-400 text-xs mt-2">{errors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="form-label">
                  Phone <span className="text-white/20">(optional)</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  data-testid="input-phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="Include international code e.g. +44 7712 345678"
                />
              </div>

              {/* Environment */}
              <div>
                <label htmlFor="environment" className="form-label">
                  What environment do you perform in? <span className="text-white/40">*</span>
                </label>
                <input
                  type="text"
                  id="environment"
                  name="environment"
                  data-testid="input-environment"
                  value={formData.environment}
                  onChange={handleChange}
                  className={`form-input ${errors.environment ? 'border-red-500/50' : ''}`}
                  placeholder="e.g., Professional motorsport, Executive leadership, Elite athletics..."
                  required
                />
                {errors.environment && (
                  <p className="text-red-400 text-xs mt-2">{errors.environment}</p>
                )}
              </div>

              {/* Drawn to Paragon */}
              <div>
                <label htmlFor="drawn_to_paragon" className="form-label">
                  What drew you to Paragon? <span className="text-white/40">*</span>
                </label>
                <textarea
                  id="drawn_to_paragon"
                  name="drawn_to_paragon"
                  data-testid="input-drawn"
                  value={formData.drawn_to_paragon}
                  onChange={handleChange}
                  className={`form-textarea ${errors.drawn_to_paragon ? 'border-red-500/50' : ''}`}
                  placeholder="Share what resonated with you about our approach..."
                  rows={4}
                  required
                />
                {errors.drawn_to_paragon && (
                  <p className="text-red-400 text-xs mt-2">{errors.drawn_to_paragon}</p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                <button
                  type="submit"
                  data-testid="submit-application-btn"
                  disabled={isSubmitting}
                  className={`btn-primary w-full md:w-auto ${
                    isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </div>
            </form>
          </AnimatedSection>

          {/* Note */}
          <AnimatedSection delay={0.3}>
            <div className="mt-16 pt-12 border-t border-white/10">
              <p className="text-[#555] text-sm">
                All enquiries are reviewed personally.<br />
                We respond to every message, typically within 7 days.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};
